/**
 * Account React Query Hooks (read-only)
 */

import {
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { accountApi } from '../api/accountApi';
import type {
  Account,
  AccountQueryParams,
} from './types';

// =============================================================================
// Query Keys
// =============================================================================

export const accountKeys = {
  all: ['accounts'] as const,
  lists: () => [...accountKeys.all, 'list'] as const,
  list: (params: AccountQueryParams) => [...accountKeys.lists(), params] as const,
  details: () => [...accountKeys.all, 'detail'] as const,
  detail: (id: string) => [...accountKeys.details(), id] as const,
  byIds: (ids: string[]) => [...accountKeys.all, 'byIds', [...ids].sort().join(',')] as const,
  search: (query: string) => [...accountKeys.all, 'search', query] as const,
};

// =============================================================================
// Query Hooks
// =============================================================================

export function useAccounts(
  params: AccountQueryParams = {},
  options?: Omit<UseQueryOptions<Account[], Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: accountKeys.list(params),
    queryFn: () => accountApi.list(params),
    staleTime: 10 * 60 * 1000, // 10 min — account data changes infrequently
    ...options,
  });
}

export function useAccount(
  id: string,
  options?: Omit<UseQueryOptions<Account, Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: accountKeys.detail(id),
    queryFn: () => accountApi.get(id),
    enabled: Boolean(id),
    staleTime: 10 * 60 * 1000,
    ...options,
  });
}

/**
 * Fetch multiple accounts by id (no customertypecode filter).
 * Use when you have a precomputed set of account ids and need every
 * one back regardless of categorization.
 */
export function useAccountsByIds(
  ids: string[],
  options?: Omit<UseQueryOptions<Account[], Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: accountKeys.byIds(ids),
    queryFn: () => accountApi.listByIds(ids),
    enabled: ids.length > 0,
    staleTime: 10 * 60 * 1000,
    ...options,
  });
}

/**
 * Search groups by name — for typeahead inputs.
 * Only fires when query is 2+ characters.
 */
export function useGroupSearch(
  query: string,
  options?: Omit<UseQueryOptions<Account[], Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: accountKeys.search(query),
    queryFn: () => accountApi.searchGroups(query),
    enabled: query.length >= 2,
    staleTime: 2 * 60 * 1000,
    ...options,
  });
}
